import React, { useMemo } from 'react';
import './ScoreThresholdSlider.css';

const MIN_SCORE = 0;
const MAX_SCORE = 10;

const clampScore = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return MIN_SCORE;
  return Math.min(MAX_SCORE, Math.max(MIN_SCORE, Math.round(parsed)));
};

/**
 * Single-handle 0-10 slider used as a "N or higher" floor.
 * A value of 0 means the filter is inactive.
 */
const ScoreThresholdSlider = ({ label, value, onChange }) => {
  const current = clampScore(value);
  const isActive = current > MIN_SCORE;

  const progress = useMemo(
    () => ((current - MIN_SCORE) / (MAX_SCORE - MIN_SCORE)) * 100,
    [current]
  );

  const sliderStyle = useMemo(() => ({
    background: `linear-gradient(90deg, var(--score-slider-fill) 0%, var(--score-slider-fill) ${progress}%, var(--score-slider-track) ${progress}%, var(--score-slider-track) 100%)`
  }), [progress]);

  const handleChange = (event) => {
    if (typeof onChange === 'function') {
      onChange(clampScore(event.target.value));
    }
  };

  return (
    <div className="score-threshold-slider">
      <div className="score-threshold-slider__header">
        <span className="score-threshold-slider__value">
          {isActive ? `${current} or higher` : 'Any'}
        </span>
        {isActive && (
          <button
            type="button"
            className="score-threshold-slider__reset"
            onClick={() => onChange && onChange(MIN_SCORE)}
            aria-label={`Reset ${label} filter`}
          >
            &times;
          </button>
        )}
      </div>

      <input
        type="range"
        min={MIN_SCORE}
        max={MAX_SCORE}
        step={1}
        value={current}
        onChange={handleChange}
        className="score-threshold-slider__input"
        style={sliderStyle}
        aria-label={`${label} minimum score`}
      />

      <div className="score-threshold-slider__scale">
        <span>{MIN_SCORE}</span>
        <span>{MAX_SCORE}</span>
      </div>
    </div>
  );
};

export default ScoreThresholdSlider;
